/** Abrir e fechar os painéis que têm ícone na barra lateral.
 *
 *  A barra lateral (PanelRail.tsx) e o ribbon usam estas mesmas funções,
 *  para o mesmo painel ficar aberto ou fechado nos dois. Pedido do Rui
 *  (18/10/2026): "tudo o que está na barra lateral tem de ter espaço no
 *  ribbon" (ver __tests__/barraNoRibbon.test.ts). */

import {
  Braces,
  Boxes,
  Camera,
  ClipboardCheck,
  Cloud,
  FileJson,
  FileSpreadsheet,
  FileText,
  Filter,
  Globe2,
  Info,
  Layers,
  ListTree,
  Map,
  Merge,
  MessageSquare,
  Palette,
  PencilRuler,
  PieChart,
  Scissors,
  ShieldCheck,
  Sparkles,
  SwatchBook,
  Table,
  TableProperties,
  type LucideIcon,
} from 'lucide-react'

import type { useAppStore } from '@core/store'
import type { RailItemId } from './railItems'

type Estado = ReturnType<typeof useAppStore.getState>

export const ICONES_DA_BARRA: Record<RailItemId, LucideIcon> = {
  ifcx: FileJson,
  'planos-corte': Scissors,
  arvore: ListTree,
  propriedades: Info,
  filtro: Filter,
  aparencia: Palette,
  colorir: SwatchBook,
  lentes: Layers,
  documento: FileSpreadsheet,
  viewpoints: Camera,
  'pins-bcf': MessageSquare,
  mapa: Map,
  nuvem: Cloud,
  'ids-painel': ShieldCheck,
  dados: TableProperties,
  estrutura: Braces,
  globo: Globe2,
  'juntar-ifc': Merge,
  'mod-bcf': MessageSquare,
  'mod-ids': ClipboardCheck,
  'mod-clash': Boxes,
  'mod-boq': PieChart,
  'mod-info-table': Table,
  'mod-ifc-edit': PencilRuler,
  'mod-assistant': Sparkles,
  'mod-pdf-review': FileText,
}

/** O painel está aberto? null para o que não é um painel (os módulos). */
export function painelAberto(s: Estado, id: RailItemId): boolean | null {
  switch (id) {
    case 'arvore':
      return !s.leftSidebarCollapsed
    case 'propriedades':
      return !s.rightSidebarCollapsed
    case 'filtro':
      return s.filterPanelOpen
    case 'aparencia':
      return s.appearancePanelOpen
    case 'colorir':
      return s.colorByPanelOpen
    case 'lentes':
      return s.lensPanelOpen
    case 'documento':
      return s.reportPanelOpen
    case 'ifcx':
      return s.ifcxPanelOpen
    case 'planos-corte':
      return s.planosCortePanelOpen
    case 'viewpoints':
      return s.viewpointsPanelOpen
    case 'pins-bcf':
      return s.bcfPinsPanelOpen
    case 'mapa':
      return s.mapPanelOpen
    case 'nuvem':
      return s.pointCloudPanelOpen
    case 'ids-painel':
      return s.idsPanelOpen
    case 'dados':
      return s.dadosPanelOpen
    case 'estrutura':
      return s.estruturaPanelOpen
    case 'globo':
      return s.globoPanelOpen
    case 'juntar-ifc':
      return s.juntarIfcOpen
    default:
      return null
  }
}

/** Abre o painel fechado, ou fecha o aberto. */
export function alternarPainel(s: Estado, id: RailItemId): void {
  const estaAberto = painelAberto(s, id)
  if (estaAberto === null) return
  switch (id) {
    case 'arvore':
      s.setLeftSidebarCollapsed(estaAberto)
      break
    case 'propriedades':
      s.setRightSidebarCollapsed(estaAberto)
      break
    case 'filtro':
      s.setFilterPanelOpen(!estaAberto)
      break
    case 'aparencia':
      s.setAppearancePanelOpen(!estaAberto)
      break
    case 'colorir':
      s.setColorByPanelOpen(!estaAberto)
      break
    case 'lentes':
      s.setLensPanelOpen(!estaAberto)
      break
    case 'documento':
      s.setReportPanelOpen(!estaAberto)
      break
    case 'ifcx':
      s.setIfcxPanelOpen(!estaAberto)
      break
    case 'planos-corte':
      s.setPlanosCortePanelOpen(!estaAberto)
      break
    case 'viewpoints':
      s.setViewpointsPanelOpen(!estaAberto)
      break
    case 'pins-bcf':
      s.setBcfPinsPanelOpen(!estaAberto)
      break
    case 'mapa':
      s.setMapPanelOpen(!estaAberto)
      break
    case 'nuvem':
      s.setPointCloudPanelOpen(!estaAberto)
      break
    case 'ids-painel':
      s.setIdsPanelOpen(!estaAberto)
      break
    case 'dados':
      s.setDadosPanelOpen(!estaAberto)
      break
    case 'estrutura':
      s.setEstruturaPanelOpen(!estaAberto)
      break
    case 'globo':
      s.setGloboPanelOpen(!estaAberto)
      break
    case 'juntar-ifc':
      s.setJuntarIfcOpen(!estaAberto)
      break
  }
}
